// src/services/firebaseconfig.js

import { markRaw } from 'vue'
import { initializeApp } from 'firebase/app'
import { getAnalytics } from 'firebase/analytics'
import {
  initializeAuth,
  connectAuthEmulator,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  browserPopupRedirectResolver,
} from 'firebase/auth'
import {
  initializeFirestore,
  connectFirestoreEmulator,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore'
import { getStorage, connectStorageEmulator } from 'firebase/storage'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check'
import {
  activateFirestoreMemoryFallback,
  shouldUseMemoryFirestoreCache,
} from '@/utils/clearFirestoreCache'

const firebaseConfig = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.VUE_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
  measurementId: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID,
}

const firebaseApp = markRaw(initializeApp(firebaseConfig))

let appCheck = null
const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined'
const isLocalhost =
  isBrowser &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

if (isBrowser && isLocalhost) {
  window.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.VUE_APP_FIREBASE_APPCHECK_DEBUG_TOKEN || true
}

// App Check's web provider is reCAPTCHA Enterprise, with a score-based key that
// lives in THIS project (gse-main-site). Keep it here: the previous reCAPTCHA v3
// key lived in the side project gsenergy-dev, and shutting that project down on
// 2026-09-28 made every token exchange return 500, so public lead capture and
// the phone system's App Check-enforced callables failed for every fresh tab
// (docs/bugs/appcheck_recaptcha_key_project_shutdown_BUG.md).
// The env names are historical: VUE_APP_RECAPTCHA_SITE_KEY is what every build
// environment provisions, and it now carries the Enterprise key.
const APP_CHECK_KEY =
  process.env.VUE_APP_FIREBASE_APPCHECK_SITE_KEY || process.env.VUE_APP_RECAPTCHA_SITE_KEY
const APPCHECK_EXPLICITLY_DISABLED = process.env.VUE_APP_APPCHECK_ENABLE === 'false'
const ENABLE_APPCHECK =
  !APPCHECK_EXPLICITLY_DISABLED &&
  (isLocalhost || process.env.VUE_APP_APPCHECK_ENABLE === 'true' || !!APP_CHECK_KEY)

if (ENABLE_APPCHECK && APP_CHECK_KEY) {
  try {
    appCheck = markRaw(
      initializeAppCheck(firebaseApp, {
        provider: new ReCaptchaEnterpriseProvider(APP_CHECK_KEY),
        isTokenAutoRefreshEnabled: true,
      })
    )
  } catch (e) {
    console.warn('[AppCheck] Initialization failed:', e)
    appCheck = null
  }
} else if (ENABLE_APPCHECK && !APP_CHECK_KEY) {
  console.error(
    '[AppCheck] Enabled but no reCAPTCHA Enterprise site key is configured. App Check-protected public calls will fail.'
  )
}

// Phase 33A: IndexedDB persistence — cached docs load instantly, then sync deltas.
// BloomFilter bug (v9.x/v10.x) is patched in v10.7+; current SDK is v10.13.2.
//
// `markRaw` is the canonical fix for Firebase issue #6087: Vue 3 reactivity
// recursively proxies the Firestore SDK instance once it enters any reactive
// context (Pinia state, ref(), reactive(), or even computed transitively).
// Once proxied, the SDK's internal Value protos (e.g. {stringValue: 'phone'}
// inside query target metadata) carry an invisible Proxy wrapper that
// Chromium's `forStorage` structured clone rejects with DataCloneError.
// `markRaw` adds a `__v_skip` flag so Vue treats the SDK instance as a leaf.
// See docs/bugs/outbound_call_disconnect_dataclone_BUG.md for the full chain.
const useMemoryFirestoreCache = shouldUseMemoryFirestoreCache()
if (useMemoryFirestoreCache) {
  console.warn(
    '[Firebase] Starting this tab with memory-only Firestore cache after persistent-cache recovery.'
  )
}

const db = markRaw(
  initializeFirestore(firebaseApp, {
    localCache: useMemoryFirestoreCache
      ? memoryLocalCache()
      : persistentLocalCache({
          tabManager: persistentMultipleTabManager(),
        }),
    experimentalAutoDetectLongPolling: true,
    ignoreUndefinedProperties: true,
  })
)

// Last-resort guard for IDB corruption that the markRaw fix above doesn't
// cover (disk quota, OS-level errors, future SDK regressions). Never issue a
// raw delete here: a live peer tab can block it and deadlock the origin. Move
// this tab to the same bounded memory-cache recovery path used by cache bumps.
if (isBrowser) {
  let idbErrorCount = 0
  const IDB_ERROR_THRESHOLD = 3
  const originalOnError = window.onerror
  window.onerror = function (msg, source, line, col, error) {
    const errorStr = String(msg) + ' ' + String(error?.message || '')
    if (
      errorStr.includes('INTERNAL ASSERTION FAILED') ||
      errorStr.includes('DataCloneError') ||
      errorStr.includes('could not be cloned')
    ) {
      idbErrorCount++
      if (idbErrorCount >= IDB_ERROR_THRESHOLD) {
        console.warn(
          '[Firebase] IndexedDB appears corrupted — switching this tab to memory cache...'
        )
        // A failure reason (cacheRefreshPolicy.RECOVERY_FAILURE_REASON_CODES): this tab
        // stays memory-only for its lifetime; a fresh tab retries persistence. No banner.
        activateFirestoreMemoryFallback(
          'indexeddb-corruption',
          'Firestore persistent storage reported repeated errors; this tab reads from the network for the rest of its life.'
        )
        window.location.reload()
        return true // suppress further error reporting
      }
    }
    if (originalOnError) return originalOnError(msg, source, line, col, error)
  }
}

// All Firebase product instances are markRaw'd defensively for the same
// reason as `db` above (Firebase issue #6087): Vue 3 reactivity recursively
// proxies any class instance that enters reactive state, and the SDK
// internals can then leak Vue Proxies into IndexedDB writes. Marking all
// product instances raw prevents the contamination at the source regardless
// of whether downstream code stashes them in Pinia state, refs, or
// computeds.
const auth = markRaw(
  initializeAuth(firebaseApp, {
    // UNCONDITIONAL list — recovery mode is a FIRESTORE cache mode and must
    // not govern auth (yMlyWsDn). It used to drop indexedDBLocalPersistence
    // here ("poisoned auth DB" from the LEGACY cache-clear); the current
    // reset deletes only firestore/* databases (clearFirestoreCache.js
    // firestoreDatabaseNames), never firebaseLocalStorageDb — so the hazard
    // is gone, and the exclusion's real effect was that any tab booted while
    // a reset marker sat in localStorage could not SEE the session stored in
    // the auth IDB: every target="_blank" customer link opened a signed-out
    // tab. If the auth IDB ever is broken, the SDK falls through this list
    // on its own — that is what the list is for.
    persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
    popupRedirectResolver: browserPopupRedirectResolver,
  })
)

const storage = markRaw(getStorage(firebaseApp))
const functions = markRaw(getFunctions(firebaseApp, 'us-central1'))

let analytics
if (process.env.NODE_ENV === 'production') {
  try {
    analytics = markRaw(getAnalytics(firebaseApp))
  } catch {
    analytics = undefined
  }
}

// Connect to emulators in local dev mode
if (process.env.VUE_APP_USE_EMULATORS === 'true') {
  console.log('[Firebase] Connecting to local emulators...')
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true })
  connectFirestoreEmulator(db, 'localhost', 8081)
  connectFunctionsEmulator(functions, 'localhost', 5001)
  connectStorageEmulator(storage, 'localhost', 9199)
  console.log(
    '[Firebase] Emulators connected (Auth:9099, Firestore:8081, Functions:5001, Storage:9199)'
  )
}

export { firebaseApp, db, auth, storage, analytics, functions, appCheck, useMemoryFirestoreCache }
